import React, { useMemo } from "react";
import type { JSX } from "react";

import cn from "classnames";

import { getChildrenRecord } from "../../../../../utils/react";
import { boxForwardRef } from "../../../Box/Box";
import { Gap } from "../../../Gap/Gap";
import { InputProvider, useInputStore } from "../../store/inputStore";
import { useCoreInputTextStore } from "../../store/store";
import { createTestIdForCoreInputTextElement } from "../../utils/getTestId";
import { InputBlock } from "../InputBlock/InputBlock";

import type { BoxComponentWithDefault } from "../../../Box/Box";
import type { IInputContainerProps } from "../../types";

import classes from "./InputContainer.styl";

const INPUT_CONTAINER_TEST_ID =
    createTestIdForCoreInputTextElement("input-container");

/**
 * Контейнер, в котором находится поле ввода и его дополнительные элементы.
 * Имеет свой собственный провайдер, что позволяет использовать общие методы и переменные из контекста.
 */
export const InputContainer = boxForwardRef<
    BoxComponentWithDefault<"input", IInputContainerProps>
>(({ children, value, onChange, mask, ...props }, ref) => {
    return (
        // eslint-disable-next-line ok-custom-rules/check-attribute
        <InputProvider value={value} onChange={onChange} mask={mask}>
            <InputContainerContent {...props} ref={ref}>
                {children}
            </InputContainerContent>
        </InputProvider>
    );
});

export const InputContainerContent = boxForwardRef<
    BoxComponentWithDefault<"input", IInputContainerProps>
>(
    (
        {
            as: Input = "input",
            children,
            prefix,
            style,
            className,
            ...inputProps
        },
        ref,
    ): JSX.Element => {
        const { state } = useCoreInputTextStore();
        const { isFocused } = useInputStore();

        const { Left, LeftAddon, Right, Hint } = useMemo(
            () => getChildrenRecord(children, prefix, {}),
            [children, prefix],
        );

        const inputContainerClasses = (() => {
            const result = {
                [classes["__focused"]]: isFocused,
            };
            if (
                state === "disabled" ||
                state === "readonly" ||
                state === "loading"
            ) {
                Object.assign(result, {
                    [classes[`__state-${state}`]]: state,
                });
            }
            return cn(classes["container"], result, className);
        })();

        return (
            <Gap
                className={inputContainerClasses}
                contentClassName={classes["container-content"]}
                style={style}
                data-tsid={INPUT_CONTAINER_TEST_ID}
            >
                {Left}
                {LeftAddon}
                <div className={classes["block"]}>
                    <InputBlock
                        as={Input}
                        isHintExisted={Boolean(Hint)}
                        {...inputProps}
                        ref={ref}
                    />
                </div>
                {Right}
            </Gap>
        );
    },
);
